import * as React from 'react';

import { captureException } from '@sentry/browser';
import { translate } from '~l10n/client';

import { UIButton } from '../buttons';

interface Props {
  children: NonNullable<React.ReactNode>;
  errorTitle?: string;
  errorText?: string;
  extraErrorInfo?: string;
  handleErrorCallback?: () => void;
  handleTryAgain: () => void;
}

interface State {
  isErrorView: boolean;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  static defaultProps = {
    errorText: translate(
      'Please refresh and try again. If problems persist, don’t hesitate to contact us for help!'
    ),
    errorTitle: translate('Hmm, something unexpected happened.'),
    extraErrorInfo: '',
    handleErrorCallback: () => {},
    handleTryAgain: () => {},
    isErrorState: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      isErrorView: false,
    };
  }

  static getDerivedStateFromError() {
    return { isErrorView: true };
  }

  componentDidCatch(error) {
    const { handleErrorCallback, extraErrorInfo } = this.props;
    const { message, stack, name } = error;
    let msg = '';

    if (extraErrorInfo) {
      msg = message + ' ' + extraErrorInfo;
    }

    const exception = new Error(msg);
    exception.stack = stack;
    exception.name = name;

    handleErrorCallback();
    captureException(exception);
  }

  render() {
    const { isErrorView } = this.state;
    const { children, errorText, handleTryAgain } = this.props;

    return (
      <>
        {isErrorView ? (
          <>
            <p className="text-sm pb-4">{errorText}</p>
            <UIButton
              onClick={() => {
                this.setState({ isErrorView: false });
                handleTryAgain();
              }}
            >
              {translate('Try Again')}
            </UIButton>
          </>
        ) : (
          children
        )}
      </>
    );
  }
}
